<template>
  <!-- operations card -->
  <div
    :class="{
      'w-full': receivedCaption,
      'w-full lg:w-[50%] xl:w-[40%] shadow-2xl': !receivedCaption,
    }"
    class="rounded-2xl w-full items-center justify-center mt-3"
  >
    <!-- original language gridview -->
    <div v-if="isOpen" class="overlay z-50" @click="closeList">
      <div
        class="overlay-content flex flex-col items-center min-w-[80%] h-[70%]"
        @click.stop
      >
        <div
          :class="{ 'space-x-reverse': locale === 'ar' }"
          class="flex flex-row space-x-4 w-full items-center justify-center h-10 py-4"
        >
          <input
            v-model="searchQuery"
            @input="filteredLanguages"
            type="text"
            :placeholder="$t('search-for-a-language')"
            class="pl-1 md:pl-4 bg-white border border-slate-900 rounded-md text-slate-900 text-xs md:text-base font-semibold w-[60%] sm:w-[40%] h-10"
          />
          <button
            @click="closeList"
            class="bg-white border border-slate-900 rounded-md text-slate-900 text-sm md:text-lg font-bold py-1 px-8 h-10"
          >
            {{ $t("close") }}
          </button>
        </div>
        <ul
          class="py-1 h-75vh grid grid-cols-1 md:grid-cols-3 2xl:grid-cols-4 gap-2 md:gap-4 overflow-y-auto"
        >
          <li
            v-for="lang in myLanguagesList.filter(
              (lang) => !unsupportedCloudStt.includes(lang.langCode)
            )"
            :key="lang.langName"
            class="flex flex-row space-x-2"
          >
            <a
              @click="selectLanguage(lang)"
              class="flex items-center gap-2 px-4 py-2 text-gray-800 font-semibold cursor-pointer text-sm md:text-base"
            >
              <i
                class="pi pi-globe text-gray-500 flex-shrink-0"
                style="font-size: 0.875rem"
                aria-hidden="true"
              ></i>
              {{ lang.langName }}
            </a>
          </li>
        </ul>
      </div>
    </div>
    <div
      v-if="!processingStarted && !isFromNotification && !receivedCaption"
      class="drag-drop-area w-full flex flex-col items-center justify-center rounded-2xl h-auto space-y-1"
      @dragover.prevent
      @drop.prevent="handleDrop"
    >
      <div v-if="oneTimeResultPendingOrderId" class="w-full max-w-md mx-auto flex flex-col items-center py-8 px-6 rounded-2xl bg-slate-50 border border-slate-200 shadow-sm space-y-5">
        <p class="text-lg font-semibold text-[#061B44] text-center">{{ $t("Still processing. Your result is not ready yet.") }}</p>
        <button
          type="button"
          :disabled="isFetchingOneTimeResult"
          @click="fetchOneTimeResult(oneTimeResultPendingOrderId!)"
          class="px-5 py-2.5 rounded-lg bg-[#061B44] text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ $t("Retry") }}
        </button>
      </div>
      <div
        v-else-if="showOneTimePayPanel && !profileStore.hasActivePaidSubscription"
        class="w-full flex justify-center py-4"
      >
        <OneTimeAudioPay
          source-type="video"
          :video-link="(userInputVideoLink || '').trim()"
          :source-lang="originalLanguage?.langCode || ''"
          @cancel="showOneTimePayPanel = false"
        />
      </div>
      <div
        v-if="!selectedVideo.name"
        class="container flex flex-col items-center space-y-2"
      >
        <img
          src="../../../assets/icons/upload.svg"
          alt=""
          class="w-[14%] lg:w-[5%] text-white fill-white"
        />
        <p class="text-sm md:text-lg text-slate-600 font-normal">
          {{ $t("drop-to-upload-or") }}
        </p>
        <div class="flex flex-row w-[50%] lg:w-[40%] h-10">
          <label
            for="forupload"
            class="cursor-pointer bg-[#4867C8] text-white mx-auto text-xs md:text-base font-bold px-4 md:px-10 flex items-center rounded-lg h-10"
          >
            {{ $t("choose-a-file") }}
          </label>
          <input
            type="file"
            accept="video/mp4, video/quicktime, video/webm, video/x-matroska, video/x-msvideo"
            @change="handleFileChange"
            ref="fileInput"
            id="forupload"
            style="display: none"
          />
        </div>
        <p class="text-sm text-slate-900 font-bold py-1">
          {{ $t("OR") }}
        </p>
        <!-- video link area -->
        <div
          class="rounded-lg px-2 flex flex-col-reverse lg:flex-row justify-center h-10 bg-white border border-slate-700 items-center w-full py-10 lg:py-0"
        >
          <div class="relative group">
            <svg
              class="h-6 w-6 cursor-pointer"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                d="m12.002 2.005c5.518 0 9.998 4.48 9.998 9.997 0 5.518-4.48 9.998-9.998 9.998-5.517 0-9.997-4.48-9.997-9.998 0-5.517 4.48-9.997 9.997-9.997zm0 8c-.414 0-.75.336-.75.75v5.5c0 .414.336.75.75.75s.75-.336.75-.75v-5.5c0-.414-.336-.75-.75-.75zm-.002-3c-.552 0-1 .448-1 1s.448 1 1 1 1-.448 1-1-.448-1-1-1z"
                fill-rule="nonzero"
              />
            </svg>

            <!-- Tooltip Div -->
            <div
              class="absolute -left-36 md:left-0 z-10 hidden p-4 text-sm text-start text-black bg-white border rounded-lg w-[300px] md:w-[420px] group-hover:block"
            >
              <p>{{ $t("examples-of-valid-links") }}</p>
              <ul class="list-disc pl-4 mt-2 text-xs md:text-sm">
                <li>{{ $t("direct-link-https-example-com-video-mp4") }}</li>
                <li>
                  {{
                    $t(
                      "google-drive-https-drive-google-com-file-d-file_id-view-usp-sharing"
                    )
                  }}
                </li>
              </ul>
              <p class="mt-2">
                {{
                  $t(
                    "make-sure-your-google-drive-link-is-set-to-anyone-with-the-link-can-view"
                  )
                }}
              </p>
            </div>
          </div>
          <input
            type="text"
            v-model="userInputVideoLink"
            @keydown.ctrl.x.exact="handleCtrlX"
            @keydown.enter.exact="() => transcriptExterneVideo()"
            :placeholder="$t('enter-direct-video-link-gDrive')"
            :class="{ 'pr-2': locale === 'ar' }"
            class="text-field w-[90%] mt-4 lg:mt-1 pb-4 h-10 text-xs md:text-base text-start white bg-white lg:bg-transparent rounded-full lg:rounded-none lg:shadow-none lg:w-full md:pl-4 pt-4 lg:pt-3 outline-none font-medium"
          />
          <div class="h-4 lg:hidden"></div>
        </div>
        <p class="font-normal text-xs md:text-sm text-slate-600 py-1">
          {{ $t("file-type-mp4-mov-webm") }} |
          {{ $t("file-size-up-to-10-gb") }} | {{ $t("max-duration-8-hours") }}
        </p>
        <div
          class="relative container w-full flex flex-row space-x-4 items-center justify-center pb-1"
        >
          <button
            v-if="!receivedCaption"
            @click="toggleOriginalLanguageList"
            :class="{
              'bg-slate-400': isFromNotification || receivedCaption,
              'space-x-reverse': locale === 'ar',
            }"
            class="w-full flex justify-center items-center space-x-4 bg-white h-10 p-3 rounded-lg border border-slate-900"
          >
            <p class="text-slate-900 font-medium text-sm md:text-base lg:text-lg">
              {{ $t("source-language") }}
            </p>
            <span
              v-if="originalLanguage.langName.length != 0"
              class="inline-flex items-center gap-1.5"
              :class="{ 'flex-row-reverse': locale === 'ar' }"
            >
              <i
                class="pi pi-globe text-slate-700 opacity-80 flex-shrink-0 text-sm md:text-base"
                aria-hidden="true"
              ></i>
              <span class="text-slate-900 font-medium text-base lg:text-lg">
                {{ getLanguageText() }}
              </span>
            </span>
            <img
              src="../../../assets/images/audio/02.svg"
              alt=""
              class="w-[2%] text-white fill-white"
            />
          </button>
        </div>
        <div v-if="oneTimeUploadOrderId && oneTimeUploadError" class="w-full text-sm text-red-600 py-1">{{ oneTimeUploadError }}</div>
        <div class="hidden md:block"></div>
        <!-- One-time pay path: only when user has no active paid subscription -->
        <div
          v-if="
            !oneTimeResultPendingOrderId &&
            !oneTimeUploadOrderId &&
            !profileStore.hasActivePaidSubscription
          "
          class="w-full max-w-md mx-auto flex flex-col items-center py-3 px-4 rounded-2xl bg-slate-50/80 border border-slate-200/80 mt-2"
        >
          <p class="text-sm text-slate-600 mb-2">{{ $t("No subscription?") }}</p>
          <p class="text-xs text-slate-500 text-center mb-3 max-w-sm">
            {{ $t("one-time_video_file_flow_hint") }}
            <span class="block mt-1">{{ $t("one-time_video_link_flow_hint") }}</span>
          </p>
          <p v-if="hasVideoLinkAndSourceLang" class="text-xs text-[#061B44] font-medium mb-2">{{ $t("one-time_video_link_ready_hint") }}</p>
          <button
            type="button"
            @click="showOneTimePayPanel = true"
            class="px-5 py-2.5 rounded-lg bg-[#061B44] text-white font-medium hover:opacity-90 transition-opacity"
          >
            {{ hasVideoLinkAndSourceLang ? $t("Pay and process video") : $t("Pay once for this order") }}
          </button>
        </div>
        <button
          :disabled="oneTimeVideoLinkSubmitting"
          @click="() => transcriptExterneVideo()"
          class="button px-20 py-1 bg-subDark text-white text-sm md:text-base lg:text-lg font-bold w-full rounded-lg h-10 disabled:opacity-50 disabled:cursor-not-allowed mt-3"
        >
          {{ $t("Transcript_Video") }}
        </button>
      </div>
      <!-- file element -->
      <div class="container items-center justify-center flex flex-col">
        <div
          v-if="selectedVideo.name.length != 0 && !receivedCaption"
          class="relative container w-full flex flex-row space-x-4 items-center justify-center pb-4"
        >
          <button
            @click="toggleOriginalLanguageList"
            :class="{ 'bg-slate-400': isFromNotification || receivedCaption }"
            class="w-full flex justify-center items-center space-x-4 bg-white h-10 p-3 rounded-lg border border-slate-900"
          >
            <p class="text-slate-900 font-medium text-sm md:text-base lg:text-lg">
              {{ $t("source-language") }}
            </p>
            <span
              v-if="originalLanguage.langName.length != 0"
              class="inline-flex items-center gap-1.5"
              :class="{ 'flex-row-reverse': locale === 'ar' }"
            >
              <i
                class="pi pi-globe text-slate-700 opacity-80 flex-shrink-0 text-sm md:text-base"
                aria-hidden="true"
              ></i>
              <span class="text-slate-900 font-medium text-sm md:text-base lg:text-lg">
                {{ getLanguageText() }}
              </span>
            </span>
            <img
              src="../../../assets/images/audio/02.svg"
              alt=""
              class="w-[3%] text-white fill-white"
            />
          </button>
        </div>
        <div
          v-if="
            selectedVideo.name.length != 0 &&
            selectedVideo.data &&
            !receivedCaption
          "
          class="flex flex-row items-center justify-between px-2 w-full rounded-lg border border-slate-900 h-10"
        >
          <div class="flex flex-row max-w-[95%]">
            <img src="../../../assets/images/audio/file.svg" class="w-6" />
            <p
              class="truncate text-base lg:text-lg pl-2 text-slate-900 font-medium"
            >
              {{ selectedVideo.name }}
            </p>
          </div>
          <div
            @click="removeFile"
            class="min-w-6 h-6 rounded-full bg-red-100 hover:bg-red-300 flex items-center justify-center cursor-pointer text-red-500 transition-colors duration-200"
            role="button"
            aria-label="Remove file"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        </div>
        <div
          v-if="
            selectedVideo.name.length != 0 &&
            selectedVideo.data &&
            !receivedCaption &&
            !isFromNotification
          "
          class="container pt-4"
        >
          <!-- <p
            v-if="!computePricing && balanceNotEnough"
            class="text-base pl-4 text-red-600 font-medium"
          >
            {{
              $t(
                "this-selected-audio-file-will-incur-a-charge-of-audiotranscriptioncost-for-transcription-services",
                { audioTranscriptionCost }
              )
            }}
          </p> -->
          <!-- <div
            v-if="balanceNotEnough && !computePricing"
            class="text-base pl-4 text-red-600 font-medium"
          >
            <p class="text-base pl-4 text-red-600 font-medium">
              {{
                $t("your-balance-is-insufficient-please-recharge-to-continue")
              }}
            </p>
            <button
              @click="navToPayment"
              class="text-lg font-bold text-blue-600"
            >
              {{ $t("clicking-here") }}
            </button>
          </div> -->

          <!-- <p
            v-if="computePricing"
            class="text-base pl-4 text-[#2152B6] font-medium"
          >
            {{ $t("calculating-file-transcription-cost-please-wait") }}
          </p> -->
        </div>
        <!-- translation language gridview -->
        <div v-if="isTargetListOpen" class="overlay z-50" @click="closeList">
          <div
            class="overlay-content flex flex-col items-center min-w-[80%] h-[70%]"
            @click.stop
          >
            <div
              :class="{ 'space-x-reverse': locale === 'ar' }"
              class="flex flex-row space-x-4 w-full items-center justify-center h-10 py-4"
            >
              <input
                v-model="searchQuery"
                @input="filteredLanguages"
                type="text"
                :placeholder="$t('search-for-a-language')"
                class="pl-4 bg-white border border-slate-900 rounded-md text-slate-900 text-xs md:text-base font-semibold w-[40%] h-10"
              />
              <button
                @click="closeList"
                class="bg-white border border-slate-900 rounded-md text-slate-900 text-lg font-bold py-1 px-8 h-10"
              >
                {{ $t("close") }}
              </button>
            </div>
            <ul
              class="py-1 h-75vh grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-4 overflow-y-auto"
            >
              <li
                v-for="lang in myLanguagesList"
                :key="lang.langName"
                class="flex flex-row space-x-2"
              >
                <a
                  @click="selectTargetLanguage(lang)"
                  class="flex items-center gap-2 px-4 py-2 text-gray-800 font-semibold cursor-pointer"
                >
                  <i
                    class="pi pi-globe text-gray-500 flex-shrink-0"
                    style="font-size: 0.875rem"
                    aria-hidden="true"
                  ></i>
                  {{ lang.langName }}
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div
          v-if="selectedVideo.name.length != 0 && !receivedCaption"
          class="flex flex-row space-x-4 items-center justify-center w-full"
        >
          <button
            @click="startProcessing"
            class="button px-20 py-1 bg-subDark text-base lg:text-lg font-bold w-full rounded-lg h-10"
          >
            <p class="text-white font-semibold text-sm md:text-base lg:text-lg">
              {{ $t("process-video-file") }}
            </p>
          </button>
        </div>
      </div>
    </div>
    <!-- processing status -->
    <div
      v-if="processingStarted && !receivedCaption && !notReadyYet"
      class="flex flex-col justify-center items-center space-y-2 h-auto py-12 p-4 md:p-0 md:py-12"
    >
      <img
        class="w-12 h-12 lg:w-20 lg:h-20 animate-spin fill-[#061B44]"
        src="../../../assets/icons/loading-loader.svg"
        alt="Loading icon"
      />
      <p class="text-base md:text-xl lg:text-2xl text-slate-900 text-center font-semibold">
        {{ processingStatus }}
      </p>
      <div
        v-if="!processingLink"
        class="bg-gray-400 rounded-full h-2.5 w-[80%] lg:w-[70%]"
      >
        <div
          class="bg-blue-700 h-2.5 rounded-full"
          :style="{ width: `${progressBar}%` }"
        ></div>
      </div>
      <p
        v-if="!processingLink"
        class="text-sm md:text-base lg:text-xl text-slate-900 text-center font-medium"
      >
        {{ $t("This_may_take_a_minute") }}
      </p>
      <p
        v-if="!processingLink"
        class="text-sm md:text-base lg:text-xl text-slate-900 text-center font-medium"
      >
        {{ selectedVideo.name }}
      </p>
    </div>
    <!-- comeback later -->
    <div
      v-if="processingStarted && !receivedCaption && notReadyYet"
      class="flex flex-col justify-center items-center space-y-2 h-auto py-12"
    >
      <img
        class="w-32 h-32 lg:w-40 lg:h-40 fill-[#061B44]"
        src="../../../assets/images/youtube/01.png"
        alt="Loading icon"
      />
      <p
        class="text-sm pt-10 lg:text-base text-[#061B44] text-center font-medium"
      >
        {{ $t("Your_video_was_sent_to_our_service_You_will_be") }}
      </p>
      <p class="text-sm lg:text-base text-[#061B44] text-center font-medium">
        {{
          $t(
            "thank-you-for-using-our-service-feel-free-to-continue-exploring-our-services"
          )
        }}
        <router-link to="/orders" class="font-semibold text-[#6193F6]">{{
          $t("Orderspage")
        }}</router-link>
      </p>
      <p class="text-sm lg:text-base text-[#061B44] text-center font-medium">
        {{ $t("if-youd-like-to-return-to-the-home-page-simply-follow") }}
        <button @click="goBackToMainPage" class="font-semibold text-[#6193F6]">
          {{ $t("the-link-below") }}
        </button>
      </p>
    </div>
    <VideoProcessingResult />
  </div>
  <!-- user consume solde -->
  <div
    v-if="showPaymentModal"
    class="overlay bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
  >
    <div class="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
      <div class="flex flex-row text-xl items-center mb-4 space-x-1">
        <i class="pi pi-credit-card text-blue-500 text-center"></i>
        <h3 class="font-semibold">{{ $t("complete-your-translation") }}</h3>
      </div>
      <p class="text-gray-700 mb-6">{{ modalMessage }}</p>
      <div class="flex justify-end space-x-4">
        <button
          @click="handleModalResponse(false)"
          class="mt-4 bg-white text-slate-900 border-slate-400 border-[1px] h-9 py-1 px-10 rounded-md font-normal text-base"
        >
          {{ t("cancel") }}
        </button>
        <button
          @click="handleModalResponse(true)"
          class="mt-4 text-white bg-slate-900 h-9 py-1 px-10 rounded-md font-normal text-base flex items-center space-x-2"
        >
          <p>{{ $t("continue") }}</p>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useVideoStore } from "../../../store/videoStore";
import { useI18n } from "vue-i18n";
import {
  API_BASE_URL,
  GENERATEUPLOADURL,
  GETPRICES,
  GOOGLEDRIVEVIDEO,
  SUMMARIZETEXT,
  TRANSCRIPT_AUDIO,
  TRANSCRIPTEXTERNVIDEO,
  ONETIME_AUDIO_UPLOAD_URL,
  ONETIME_AUDIO_CONFIRM_UPLOAD,
  ONETIME_AUDIO_CONFIRM_VIDEO_LINK,
  ONETIME_AUDIO_RESULT,
} from "../../../utils/appConst";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { toBlobURL } from "@ffmpeg/util";
import { toast } from "vue3-toastify";
import { useUserProfile } from "../../../store/userProfile";
import { useUserActivities } from "../../../store/userActivities";
import { useAuthStore } from "../../../store/auth";
import { useRouter, useRoute } from "vue-router";
import { CaptionModel } from "../../../utils/captionModel";
import {
  languages,
  Language,
  unsupportedCloudStt,
} from "../../../utils/languageList";
import VideoProcessingResult from "../../modules/video/video_processing_result.vue";
import OneTimeAudioPay from "../audio/one_time_audio_pay.vue";
import servicesPricing from "../../../utils/servicesPricing";
import {
  parseWebVTTToCaption,
  convertCaptionToWebVTT,
  segmentsListForCaptions,
  type CaptionItem,
} from "../../../utils/webvtt";
import { useHead } from "@vueuse/head";

const { t, locale } = useI18n();
const authStore = useAuthStore();
const profileStore = useUserProfile();
const activitesStore = useUserActivities();
const router = useRouter();
const route = useRoute();
const vs = useVideoStore();
let durationInUnits = 0;
const ffmpeg = new FFmpeg();
const fontSizeClass = ref("text-lg");
const transcriptExterneVideoUrl = `${API_BASE_URL}${TRANSCRIPTEXTERNVIDEO}`;
const transcriptAudioUrl = `${API_BASE_URL}${TRANSCRIPT_AUDIO}`;
const generateUploadUrl = `${API_BASE_URL}${GENERATEUPLOADURL}`;
const getPrices = `${API_BASE_URL}${GETPRICES}`;
const summarizeTextUrl = `${API_BASE_URL}${SUMMARIZETEXT}`;
const transcriptGoogleDriveVideoUrl = `${API_BASE_URL}${GOOGLEDRIVEVIDEO}`;
const ffmpegbaseURL = "https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm";
const showPaymentModal = ref(false);
const isPaymentProcessingLink = ref(false);
const modalMessage = ref("");
const showOneTimePayPanel = ref(false);
watch(
  () => profileStore.hasActivePaidSubscription,
  (active) => {
    if (active) showOneTimePayPanel.value = false;
  }
);
const oneTimeUploadOrderId = ref<string | null>(null);
const oneTimeResultPendingOrderId = ref<string | null>(null);
const isFetchingOneTimeResult = ref(false);
const oneTimeUploadDone = ref(false);
const oneTimeUploading = ref(false);
const oneTimeUploadError = ref("");
const oneTimeUploadSourceLang = ref("");
const oneTimeVideoLinkSubmitting = ref(false);
const processingStarted = computed({
  get: () => vs.processingStarted,
  set: (value) => (vs.processingStarted = value),
});
const originalJsonContent = computed({
  get: () => vs.originalJsonContent,
  set: (value) => (vs.originalJsonContent = value),
});
const receivedCaption = computed({
  get: () => vs.receivedCaption,
  set: (value) => (vs.receivedCaption = value),
});
const myLanguagesList = computed({
  get: () => vs.myLanguagesList,
  set: (value) => (vs.myLanguagesList = value),
});
const isOpen = computed({
  get: () => vs.isOpen,
  set: (value) => (vs.isOpen = value),
});
const searchQuery = computed({
  get: () => vs.searchQuery,
  set: (value) => (vs.searchQuery = value),
});
const isFromNotification = computed({
  get: () => vs.isFromNotification,
  set: (value) => (vs.isFromNotification = value),
});
const originalLanguage = computed({
  get: () => vs.originalLanguage,
  set: (value) => (vs.originalLanguage = value),
});
const selectedVideo = computed({
  get: () => vs.selectedVideo,
  set: (value) => (vs.selectedVideo = value),
});
const userInputVideoLink = computed({
  get: () => vs.userInputVideoLink,
  set: (value) => (vs.userInputVideoLink = value),
});
const hasVideoLinkAndSourceLang = computed(() => {
  const link = typeof vs.userInputVideoLink === "string" ? vs.userInputVideoLink.trim() : "";
  const lang = vs.originalLanguage?.langCode;
  return link.length > 0 && !!lang && String(lang).trim().length > 0;
});
const computePricing = computed({
  get: () => vs.computePricing,
  set: (value) => (vs.computePricing = value),
});
const balanceNotEnough = computed({
  get: () => vs.balanceNotEnough,
  set: (value) => (vs.balanceNotEnough = value),
});
const isTargetListOpen = computed({
  get: () => vs.isTargetListOpen,
  set: (value) => (vs.isTargetListOpen = value),
});
const isTargetLanguageSelected = computed({
  get: () => vs.isTargetLanguageSelected,
  set: (value) => (vs.isTargetLanguageSelected = value),
});

const translationLanguage = computed({
  get: () => vs.translationLanguage,
  set: (value) => (vs.translationLanguage = value),
});
const notReadyYet = computed({
  get: () => vs.notReadyYet,
  set: (value) => (vs.notReadyYet = value),
});
const processingStatus = computed({
  get: () => vs.processingStatus,
  set: (value) => (vs.processingStatus = value),
});
const progressBar = computed({
  get: () => vs.progressBar,
  set: (value) => (vs.progressBar = value),
});

const fileUploadPercentage = computed({
  get: () => vs.fileUploadPercentage,
  set: (value) => (vs.fileUploadPercentage = value),
});
const fileUploaded = computed({
  get: () => vs.fileUploaded,
  set: (value) => (vs.fileUploaded = value),
});
const ffmpegPercentage = computed({
  get: () => vs.ffmpegPercentage,
  set: (value) => (vs.ffmpegPercentage = value),
});
const currentRequestId = computed({
  get: () => vs.currentRequestId,
  set: (value) => (vs.currentRequestId = value),
});
const translatedVideoSubtitle = computed({
  get: () => vs.translatedVideoSubtitle,
  set: (value) => (vs.translatedVideoSubtitle = value),
});
const originalVideoSubtitle = computed({
  get: () => vs.originalVideoSubtitle,
  set: (value) => (vs.originalVideoSubtitle = value),
});
const prebuildVideoSubtitle = computed({
  get: () => vs.prebuildVideoSubtitle,
  set: (value) => (vs.prebuildVideoSubtitle = value),
});
const videoPlayer = computed({
  get: () => vs.videoPlayer,
  set: (value) => (vs.videoPlayer = value),
});
const originalText = computed({
  get: () => vs.originalText,
  set: (value) => (vs.originalText = value),
});
const videoUrl = computed({
  get: () => vs.videoUrl,
  set: (value) => (vs.videoUrl = value),
});
const textTranslationPrice = computed({
  get: () => vs.textTranslationPrice,
  set: (value) => (vs.textTranslationPrice = value),
});
const fontFamily = computed({
  get: () => vs.fontFamily,
  set: (value) => (vs.fontFamily = value),
});
const defaultOriginalText = computed({
  get: () => vs.defaultOriginalText,
  set: (value) => (vs.defaultOriginalText = value),
});
const defaultTranslatedText = computed({
  get: () => vs.defaultTranslatedText,
  set: (value) => (vs.defaultTranslatedText = value),
});
const translatedText = computed({
  get: () => vs.translatedText,
  set: (value) => (vs.translatedText = value),
});
const defaultOriginalVideoSubtitle = computed({
  get: () => vs.defaultOriginalVideoSubtitle,
  set: (value) => (vs.defaultOriginalVideoSubtitle = value),
});
const defaultTranslatedVideoSubtitle = computed({
  get: () => vs.defaultTranslatedVideoSubtitle,
  set: (value) => (vs.defaultTranslatedVideoSubtitle = value),
});
const processingLink = computed({
  get: () => vs.processingLink,
  set: (value) => (vs.processingLink = value),
});

async function confirmVideoLinkWithRetry(orderId: string, maxAttempts = 3, delayMs = 2000): Promise<{ ok: boolean; error?: string }> {
  console.log("[OneTimeVideoLink] confirmVideoLinkWithRetry orderId=", orderId, "maxAttempts=", maxAttempts);
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const res = await fetch(`${API_BASE_URL}${ONETIME_AUDIO_CONFIRM_VIDEO_LINK}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
      },
      body: JSON.stringify({ orderId }),
    });
    const data = await res.json();
    console.log("[OneTimeVideoLink] confirmVideoLink attempt=", attempt, "status=", res.status, "ok=", res.ok, "error=", data.error);
    if (res.ok) return { ok: true };
    const msg = data.error || "Request failed";
    const isPaymentNotCompleted = msg.toLowerCase().includes("payment not completed");
    if (isPaymentNotCompleted && attempt < maxAttempts) {
      console.log("[OneTimeVideoLink] Payment not yet recorded, retrying in", delayMs, "ms");
      await new Promise((r) => setTimeout(r, delayMs));
      continue;
    }
    return { ok: false, error: msg };
  }
  return { ok: false, error: "Request failed" };
}

onMounted(async () => {
  if (route.query.onetime === "success" && route.query.orderId && route.query.type === "video") {
    const orderId = route.query.orderId.toString();
    oneTimeUploadOrderId.value = orderId;
    oneTimeUploadError.value = "";
    oneTimeUploadSourceLang.value = "";
    const storedLink = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("onetime_video_link") : null;
    const hadVideoLink = !!storedLink && String(storedLink).trim().length > 0;
    console.log("[OneTimeVideoLink] Return from PayPal, showing order under process orderId=", orderId, "hadVideoLink=", hadVideoLink);
    if (hadVideoLink && storedLink) {
      userInputVideoLink.value = String(storedLink).trim();
      oneTimeUploadDone.value = true;
      notReadyYet.value = true;
      processingStarted.value = true;
      try {
        sessionStorage.removeItem("onetime_video_link");
        sessionStorage.removeItem("onetime_video_sourceLang");
      } catch (_) {}
    } else {
      oneTimeUploadDone.value = false;
      toast.success(t("Payment successful. Upload your video or paste a video link."));
    }
  }
  if (route.query.onetime === "cancelled" && route.query.type === "video") {
    toast(t("Payment was cancelled."), { type: "info", theme: "light" });
  }
  if (!ffmpeg.loaded) {
    console.log("Preloading FFmpeg...");
    await ffmpeg.load({
      coreURL: await toBlobURL(
        `${ffmpegbaseURL}/ffmpeg-core.js`,
        "text/javascript"
      ),
      wasmURL: await toBlobURL(
        `${ffmpegbaseURL}/ffmpeg-core.wasm`,
        "application/wasm"
      ),
    });
    console.log("FFmpeg preloaded!");
  }
});

const toggleOriginalLanguageList = () => {
  if (receivedCaption.value || isFromNotification.value) {
    return;
  }
  isOpen.value = !isOpen.value;
};

const getLanguageText = () => {
  return (
    removeParentheses(originalLanguage.value.langName) || "Original Language"
  );
};

const removeParentheses = (langName: string) => {
  return langName.replace(/\s*\(.*?\)\s*/g, "").trim();
};

const closeList = () => {
  isOpen.value = false;
  isTargetListOpen.value = false;
  resetFilter();
};
const resetFilter = () => {
  searchQuery.value = "";
  myLanguagesList.value = languages;
};

const filteredLanguages = () => {
  if (searchQuery.value.length == 0) {
    myLanguagesList.value = languages;
  } else {
    const lowerCaseQuery = searchQuery.value.toLowerCase();
    myLanguagesList.value = languages.filter((langs) =>
      langs.langName.toLowerCase().includes(lowerCaseQuery)
    );
  }
};

const goBackToMainPage = () => {
  if (notReadyYet.value && processingStarted.value) {
    notReadyYet.value = false;
    processingStarted.value = false;
    processingLink.value = false;
    selectedVideo.value = { name: "", data: null };
  }
};

const selectLanguage = (lang: Language) => {
  originalLanguage.value = { ...lang };
  isOpen.value = false;
};

watch(originalVideoSubtitle, () => {
  const tracks = videoPlayer.value?.textTracks!();
  if (tracks) {
    for (let i = tracks.length - 1; i >= 0; i--) {
      const track = tracks[i];
      if (track.language === originalLanguage.value.langCode) {
        videoPlayer.value?.removeRemoteTextTrack(track);
        videoPlayer.value?.addRemoteTextTrack(
          {
            src: `data:text/vtt;charset=utf-8,${encodeURIComponent(
              originalVideoSubtitle.value
            )}`,
            kind: "captions",
            srclang: originalLanguage.value.langCode,
            label: originalLanguage.value.langName,
            mode: "showing",
          },
          false
        );
      } else {
        track.mode = "disabled";
      }
    }
  }
});

watch(translatedVideoSubtitle, () => {
  const tracks = videoPlayer.value?.textTracks!();
  if (tracks) {
    for (let i = tracks.length - 1; i >= 0; i--) {
      const track = tracks[i];
      if (track.language === translationLanguage.value.langCode) {
        videoPlayer.value?.removeRemoteTextTrack(track);
        videoPlayer.value?.addRemoteTextTrack(
          {
            src: `data:text/vtt;charset=utf-8,${encodeURIComponent(
              translatedVideoSubtitle.value
            )}`,
            kind: "captions",
            srclang: translationLanguage.value.langCode,
            label: translationLanguage.value.langName,
            mode: "showing",
          },
          false
        );
      } else {
        track.mode = "disabled";
      }
    }
  }
});

const handleDrop = async (event: any) => {
  const file = event.dataTransfer.files[0];
  if (file.size > 10000 * 1024 * 1024) {
    toast(t("error-file-size-exceeds-10GB"), {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
    return;
  }
  if (
    file &&
    (file.type === "video/mp4" ||
      file.type === "video/quicktime" ||
      file.type === "video/x-matroska" ||
      file.type === "video/x-msvideo" ||
      file.type === "video/webm")
  ) {
    const reader = new FileReader();
    videoUrl.value = URL.createObjectURL(file);
    reader.onload = (e) => {
      const fileContent = e.target?.result;
      const audioElement = document.createElement("audio");
      const objectURL = URL.createObjectURL(file);
      audioElement.src = objectURL;
      audioElement.addEventListener("loadedmetadata", () => {
        const durationInSeconds = audioElement.duration;
        const maxDurationSeconds = 10 * 3600; // 10 hours in seconds
        if (durationInSeconds > maxDurationSeconds) {
          toast(t("this-clip-exceeds-the-8-h"), {
            autoClose: 3500,
            type: "error",
            theme: "light",
          });
          URL.revokeObjectURL(objectURL);
          selectedVideo.value.data = null;
          selectedVideo.value.name = "";
          videoUrl.value = "";
          return;
        }
        durationInUnits = Math.ceil(durationInSeconds / 30);
        computeAudioCost();
        URL.revokeObjectURL(objectURL);
      });
      selectedVideo.value.data =
        fileContent instanceof ArrayBuffer ? fileContent : null;
      selectedVideo.value.name = file.name;
    };
    reader.readAsArrayBuffer(file);
    if (isFromNotification.value) {
      //await translateText();

      if (videoPlayer.value) {
        videoPlayer.value?.ready(function () {
          videoPlayer.value?.addRemoteTextTrack(
            {
              src: `data:text/vtt;charset=utf-8,${encodeURIComponent(
                originalVideoSubtitle.value
              )}`,
              kind: "captions",
              srclang: originalLanguage.value.langCode,
              label: originalLanguage.value.langName,
              mode: "showing",
            },
            false
          );
          videoPlayer.value?.addRemoteTextTrack(
            {
              src: `data:text/vtt;charset=utf-8,${encodeURIComponent(
                translatedVideoSubtitle.value
              )}`,
              kind: "captions",
              srclang: translationLanguage.value.langCode,
              label: translationLanguage.value.langName,
              mode: "showing",
            },
            false
          );
        });

        videoPlayer.value?.src({ type: "video/mp4", src: videoUrl.value });
        videoPlayer.value?.on("timeupdate", function () {});
        updateTextTrackStyles();
      }
    }
  }
};

const handleModalResponse = async (confirmed: boolean) => {
  showPaymentModal.value = false;
  if (confirmed) {
    balanceNotEnough.value = false;
    if (isPaymentProcessingLink.value) {
      await transcriptExterneVideo(true);
    }
  }
  isPaymentProcessingLink.value = false;
};

const handleFileChange = async (event: any) => {
  const file = event.target.files[0];
  if (file.size > 10000 * 1024 * 1024) {
    toast(t("error-file-size-exceeds-10GB"), {
      autoClose: 2500,
      type: "error",
      theme: "light",
    });
    return;
  }
  if (
    file &&
    (file.type === "video/mp4" ||
      file.type === "video/quicktime" ||
      file.type === "video/x-matroska" ||
      file.type === "video/x-msvideo" ||
      file.type === "video/webm")
  ) {
    const reader = new FileReader();
    videoUrl.value = URL.createObjectURL(file);
    reader.onload = (e) => {
      const fileContent = e.target?.result;
      const audioElement = document.createElement("audio");
      const objectURL = URL.createObjectURL(file);
      audioElement.src = objectURL;
      audioElement.addEventListener("loadedmetadata", () => {
        const durationInSeconds = audioElement.duration;
        const maxDurationSeconds = 10 * 3600; // 10 hours in seconds
        if (durationInSeconds > maxDurationSeconds) {
          toast(t("this-clip-exceeds-the-8-h"), {
            autoClose: 3500,
            type: "error",
            theme: "light",
          });
          URL.revokeObjectURL(objectURL);
          selectedVideo.value.data = null;
          selectedVideo.value.name = "";
          videoUrl.value = "";
          return;
        }
        durationInUnits = Math.ceil(durationInSeconds / 30);
        computeAudioCost();
        URL.revokeObjectURL(objectURL);
      });
      selectedVideo.value.data =
        fileContent instanceof ArrayBuffer ? fileContent : null;
      selectedVideo.value.name = file.name;
    };
    reader.readAsArrayBuffer(file);
    if (isFromNotification.value) {
      //await translateText();

      if (videoPlayer.value) {
        videoPlayer.value?.ready(function () {
          videoPlayer.value?.addRemoteTextTrack(
            {
              src: `data:text/vtt;charset=utf-8,${encodeURIComponent(
                originalVideoSubtitle.value
              )}`,
              kind: "captions",
              srclang: originalLanguage.value.langCode,
              label: originalLanguage.value.langName,
              mode: "showing",
            },
            false
          );
          videoPlayer.value?.addRemoteTextTrack(
            {
              src: `data:text/vtt;charset=utf-8,${encodeURIComponent(
                translatedVideoSubtitle.value
              )}`,
              kind: "captions",
              srclang: translationLanguage.value.langCode,
              label: translationLanguage.value.langName,
              mode: "showing",
            },
            false
          );
        });

        videoPlayer.value?.src({ type: "video/mp4", src: videoUrl.value });
        videoPlayer.value?.on("timeupdate", function () {});
        updateTextTrackStyles();
      }
    }
  }
};

const handleCtrlX = (event: any) => {
  if (event.key === "x" && event.ctrlKey) {
    event.preventDefault();
    const inputElement = event.target;
    inputElement.select();
    document.execCommand("cut");
  }
};

const submitOneTimeVideoLink = async () => {
  const orderId = oneTimeUploadOrderId.value;
  if (!orderId || !authStore.token) return;
  const link = userInputVideoLink.value?.trim();
  if (!link) return;
  if (!originalLanguage.value?.langCode) {
    toast(t("please-select-the-source-language-to"), { autoClose: 2500, type: "error", theme: "light" });
    return;
  }
  oneTimeUploadError.value = "";
  oneTimeVideoLinkSubmitting.value = true;
  processingStarted.value = true;
  processingStatus.value = t("send-a-request-to-the-server");
  try {
    const res = await fetch(`${API_BASE_URL}${ONETIME_AUDIO_CONFIRM_VIDEO_LINK}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
      },
      body: JSON.stringify({
        orderId,
        videoLink: link,
        sourceLang: originalLanguage.value.langCode,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Request failed");
    oneTimeUploadDone.value = true;
    notReadyYet.value = true;
  } catch (e) {
    oneTimeUploadError.value = (e as Error).message || "Request failed";
    toast.error(oneTimeUploadError.value, { autoClose: 3000, theme: "light" });
  } finally {
    oneTimeVideoLinkSubmitting.value = false;
    if (!oneTimeUploadDone.value) processingStarted.value = false;
  }
};

/** Clears external URL state and stops Video.js so a file-only result does not keep playing a previous Drive/direct link. */
const clearOneTimeResultVideoPlayer = () => {
  userInputVideoLink.value = "";
  vs.videoUrl = "";
  vs.hasVideo = false;
  const p = videoPlayer.value as any;
  if (!p) return;
  try {
    p.pause();
  } catch {
    /* ignore */
  }
  try {
    if (typeof p.reset === "function") {
      p.reset();
      return;
    }
  } catch {
    /* ignore */
  }
  try {
    p.src({ type: "video/mp4", src: "" });
  } catch {
    /* ignore */
  }
};

const fetchOneTimeResult = async (orderId: string) => {
  try {
    isFetchingOneTimeResult.value = true;
    // Drop stale file bytes/name so the result view never shows "file attached" without a real re-selected file
    selectedVideo.value.data = null;
    selectedVideo.value.name = "";
    const res = await fetch(
      `${API_BASE_URL}${ONETIME_AUDIO_RESULT}?orderId=${encodeURIComponent(orderId)}`,
      { headers: { Authorization: `Bearer ${authStore.token}` } }
    );
    const data = await res.json();
    if (!res.ok) {
      if (res.status === 202) {
        oneTimeResultPendingOrderId.value = orderId;
        return;
      }
      throw new Error(data.error || "Failed to load result");
    }
    oneTimeResultPendingOrderId.value = null;
    receivedCaption.value = true;
    isFromNotification.value = true;
    if (data.voiceOverUrl && String(data.voiceOverUrl).trim()) {
      const url = String(data.voiceOverUrl).trim();
      vs.audioContentTranslated = url;
      console.log("[OneTimeResult][Video] voice over present, url set in store, url=", url);
    } else {
      vs.audioContentTranslated = "";
      console.log("[OneTimeResult][Video] voice over: there is no url, store cleared");
    }
    const videoLinkTrimmed =
      data.videoLink && String(data.videoLink).trim().length > 0
        ? String(data.videoLink).trim()
        : "";
    if (videoLinkTrimmed) {
      userInputVideoLink.value = videoLinkTrimmed;
    } else {
      // Must clear when API omits videoLink; otherwise prior Drive/link result leaves URL set and hides the upload panel
      clearOneTimeResultVideoPlayer();
    }
    if (data.fileTitle && String(data.fileTitle).trim()) {
      const name = String(data.fileTitle).trim();
      // Hint for re-selecting the same file for preview only — do not set selectedVideo.name without bytes (would look like file is already attached).
      vs.fileName = name;
      selectedVideo.value.name = "";
      selectedVideo.value.data = null;
      console.log("[OneTimeResult][Video] orderId=", orderId, "fileTitle hint for preview (vs.fileName):", name);
    } else {
      console.log("[OneTimeResult][Video] orderId=", orderId, "no fileTitle in response, fileTitle=", data.fileTitle);
    }
    originalJsonContent.value = JSON.stringify(data.result);
    const result = data.result;
    const isArrayFormat = Array.isArray(result);
    const firstResult = isArrayFormat && result.length > 0 ? result[0] : result;
    if (result && (isArrayFormat || (typeof result === "object" && result !== null))) {
      if (!isArrayFormat && typeof result === "object") {
        if (isSonioxFormat(result)) {
          originalText.value = extractTextFromSoniox(result);
          defaultOriginalText.value = extractTextFromSoniox(result);
          prebuildVideoSubtitle.value = subtitleGenerationSoniox(result);
          originalVideoSubtitle.value = subtitleGenerationSoniox(result);
          defaultOriginalVideoSubtitle.value = subtitleGenerationSoniox(result);
        } else {
          const extractData = [result];
          originalText.value = extractText(extractData);
          defaultOriginalText.value = extractText(extractData);
          prebuildVideoSubtitle.value = subtitleGeneration(extractData);
          originalVideoSubtitle.value = subtitleGeneration(extractData);
          defaultOriginalVideoSubtitle.value = subtitleGeneration(extractData);
        }
      } else if (isArrayFormat && firstResult) {
        if (isSonioxFormat(firstResult)) {
          originalText.value = extractTextFromSoniox(firstResult);
          defaultOriginalText.value = extractTextFromSoniox(firstResult);
          prebuildVideoSubtitle.value = subtitleGenerationSoniox(firstResult);
          originalVideoSubtitle.value = subtitleGenerationSoniox(firstResult);
          defaultOriginalVideoSubtitle.value = subtitleGenerationSoniox(firstResult);
        } else {
          originalText.value = extractText(result);
          defaultOriginalText.value = extractText(result);
          prebuildVideoSubtitle.value = subtitleGeneration(result);
          originalVideoSubtitle.value = subtitleGeneration(result);
          defaultOriginalVideoSubtitle.value = subtitleGeneration(result);
        }
      }
    }
    if (data.translation != null) {
      const originalCaptions = parseWebVTTToCaption(originalVideoSubtitle.value || "");
      const segmentsList = segmentsListForCaptions(data.translation.segments, originalCaptions);
      console.log("[Translation][Video] translation present, segmentsList length=", segmentsList.length, "originalCaptions length=", originalCaptions.length);
      if (segmentsList.length > 0) {
        const translationText = segmentsList.join("\n");
        defaultTranslatedText.value = translationText;
        translatedText.value = translationText;
        console.log("[Translation][Video] text translation length=", translationText.length, "preview=", translationText.slice(0, 500) + (translationText.length > 500 ? "…" : ""));
        if (data.targetLang && originalCaptions.length > 0) {
          const translatedCaptions: CaptionItem[] = originalCaptions.map((cap, i) => {
            let text: string;
            if (segmentsList.length > originalCaptions.length) {
              const startIdx = Math.round((i * segmentsList.length) / originalCaptions.length);
              const endIdx = Math.round(((i + 1) * segmentsList.length) / originalCaptions.length);
              const parts = segmentsList.slice(startIdx, endIdx).filter(Boolean);
              text = parts.length > 0 ? parts.join(" ") : (segmentsList[i] ?? segmentsList[segmentsList.length - 1] ?? cap.text);
            } else {
              text = segmentsList[i] ?? segmentsList[segmentsList.length - 1] ?? cap.text;
            }
            return { ...cap, text };
          });
          const webvtt = convertCaptionToWebVTT(translatedCaptions);
          console.log("[Translation][Video] caption translation: webvtt length=", webvtt.length, "preview=", webvtt.slice(0, 500) + (webvtt.length > 500 ? "…" : ""));
          translatedVideoSubtitle.value = webvtt;
          defaultTranslatedVideoSubtitle.value = webvtt;
        }
      }
    }
    if (data.originalLang) {
      const orLang = languages.find((l: { langCode: string }) => l.langCode === data.originalLang) || { langName: "English", langCode: "en", countryCode: "us" };
      originalLanguage.value = orLang;
    }
    if (data.targetLang) {
      const targetLangInfo = languages.find(
        (l: Language) => l.langCode === data.targetLang || (typeof l.langCode === "string" && l.langCode.startsWith((data.targetLang || "").split("-")[0]))
      );
      if (targetLangInfo) translationLanguage.value = { ...targetLangInfo };
    }
    notReadyYet.value = false;
  } catch (e) {
    console.error("fetchOneTimeResult:", e);
    toast(t("were-fixing-a-technical-issue-so-please-try"), {
      autoClose: 3000,
      type: "error",
      theme: "light",
    });
  } finally {
    isFetchingOneTimeResult.value = false;
  }
};

// When user starts a new transcription (e.g. from parent), receivedCaption becomes false;
// clear one-time state so the "Pay once for this order" button is visible again.
watch(receivedCaption, (val) => {
  if (!val) {
    oneTimeUploadOrderId.value = null;
    oneTimeResultPendingOrderId.value = null;
    showOneTimePayPanel.value = false;
    oneTimeUploadSourceLang.value = "";
  }
});

watch(
  () => [route.query.onetime, route.query.orderId, route.query.type, route.query.onetime_order],
  () => {
    if (route.query.onetime === "success" && route.query.orderId && route.query.type === "video") {
      oneTimeUploadOrderId.value = route.query.orderId.toString();
      oneTimeUploadDone.value = false;
      oneTimeUploadError.value = "";
      oneTimeUploadSourceLang.value = "";
    }
    const queryType = route.query.type?.toString();
    const onetimeOrderId = route.query.onetime_order?.toString() || route.query.orderId?.toString();
    if (queryType === "onetime_result" && onetimeOrderId) {
      selectedVideo.value.data = null;
      selectedVideo.value.name = "";
      vs.fileName = "";
      clearOneTimeResultVideoPlayer();
      fetchOneTimeResult(onetimeOrderId);
    }
  },
  { immediate: true }
);

const transcriptExterneVideo = async (extendPayment?: boolean) => {
  try {
    currentRequestId.value = "";
    if (!authStore.token || authStore.token.length == 0) {
      authStore.setAuthPop(true);
      return;
    }
    if (userInputVideoLink.value.trim().length == 0) {
      return;
    }
    if (originalLanguage.value.countryCode.length === 0) {
      toast(t("please-select-the-source-language-to"), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
      return;
    }
    if (oneTimeUploadOrderId.value) {
      await submitOneTimeVideoLink();
      return;
    }
    const userSubscription = profileStore.subscriptionType;
    if (userSubscription === "FREE") {
      toast(t("subscription-required-please-upgrade-your-plan"), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
      return;
    }
    if (userSubscription != "CONSUMEASYOUPAY") {
      const currentTimestamp = new Date(new Date().toISOString());
      const subscriptionEndDate = new Date(profileStore.subscriptionEndDate);
      if (currentTimestamp > subscriptionEndDate) {
        toast(
          t("your-subscription-has-expired-please-renew-your-subscription"),
          {
            autoClose: 2500,
            type: "error",
            theme: "light",
          }
        );
        return;
      }
    }
    // handle google drive case
    if (isValidGoogleDriveLink(userInputVideoLink.value.trim())) {
      await transcriptGoogleDriveVideo(extendPayment);
      return;
    }
    // other case
    if (
      !userInputVideoLink.value.toLowerCase().endsWith(".mp4") &&
      !userInputVideoLink.value.toLowerCase().endsWith(".mov") &&
      !userInputVideoLink.value.toLowerCase().endsWith(".webm") &&
      !userInputVideoLink.value.toLowerCase().endsWith(".mkv") &&
      !userInputVideoLink.value.toLowerCase().endsWith(".avi")
    ) {
      toast(t("the-provided-video-link-is-not-an-mp4-mov-webm-file"), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
      return;
    }
    processingLink.value = true;
    processingStarted.value = true;
    notReadyYet.value = false;
    processingStatus.value = t("send-a-request-to-the-server");
    const response = await fetch(transcriptExterneVideoUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({
        videoLink: userInputVideoLink.value,
        originalLang: originalLanguage.value.langCode,
        extendPayment,
      }),
    });
    const result = await response.json();
    console.log("result ----------", result);

    if (result.error) {
      switch (result.error) {
        case "Authorization header missing":
        case "Invalid token":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/");
          break;
        case "The balance is insufficient":
          const videoUnits = result.videoUnits || 0;
          if (userSubscription === "CONSUMEASYOUPAY") {
            const cost = sttCostFromDeposit(videoUnits / 2);
            const currentTimestamp = new Date(new Date().toISOString());
            const subscriptionEndDate = new Date(
              profileStore.subscriptionEndDate
            );
            if (currentTimestamp > subscriptionEndDate) {
              balanceNotEnough.value = true;
              toast(
                t("your-balance-is-insufficient-please-recharge-to-continue"),
                {
                  autoClose: 2500,
                  type: "error",
                  theme: "light",
                }
              );
              await new Promise((resolve) => setTimeout(resolve, 2500));
              window.location.reload();
              break;
            }

            const shortfallCost = cost - profileStore.userDeposit;
            const shortfallChars = Math.ceil(
              shortfallCost / servicesPricing.speechToText
            );
            if (shortfallChars > profileStore.speechToTextVolume) {
              balanceNotEnough.value = true;
              toast(
                t("your-balance-is-insufficient-please-recharge-to-continue"),
                {
                  autoClose: 2500,
                  type: "error",
                  theme: "light",
                }
              );
              await new Promise((resolve) => setTimeout(resolve, 2500));
              window.location.reload();
              break;
            }

            modalMessage.value = t("your-deposit-balance-is-not-enough");
            showPaymentModal.value = true;
            isPaymentProcessingLink.value = true;
            processingStarted.value = false;
            processingLink.value = false;
            break;
          } else {
            const shortfallCost =
              videoUnits / 2 - profileStore.speechToTextVolume;
            const shortfallChars = sttCostFromDeposit(shortfallCost);
            if (shortfallChars > profileStore.userDeposit) {
              balanceNotEnough.value = true;
              toast(
                t("your-balance-is-insufficient-please-recharge-to-continue"),
                {
                  autoClose: 2500,
                  type: "error",
                  theme: "light",
                }
              );
              await new Promise((resolve) => setTimeout(resolve, 2500));
              window.location.reload();
              break;
            }

            modalMessage.value = t("your-subscription-balance-is-not");
            showPaymentModal.value = true;
            processingStarted.value = false;
            isPaymentProcessingLink.value = true;
            processingLink.value = false;
            break;
          }
        case "Audio duration exceeds maximum":
          toast(t("this-clip-exceeds-the-8-h"), {
            autoClose: 3500,
            type: "warning",
            theme: "light",
          });
          break;
        case "Invalid duration":
          toast(t("there-was-an-issue-processing-link"), {
            autoClose: 3500,
            type: "error",
            theme: "light",
          });
          break;
        default:
          if (
            typeof result.error === "string" &&
            result.error.toLowerCase().includes("already processing")
          ) {
            toast(
              result.error || t("file-already-processing-please-wait"),
              {
                autoClose: 5000,
                type: "error",
                theme: "light",
              }
            );
            processingStarted.value = false;
            processingLink.value = false;
          } else {
            toast(t('were-fixing-a-technical-issue-so-please-try'), {
              autoClose: 3000,
              type: "error",
              theme: "light",
            });
            await new Promise((resolve) => setTimeout(resolve, 2500));
            window.location.reload();
          }
          break;
      }
      processingStarted.value = false;
      processingLink.value = false;
      return;
    }

    processingStatus.value = t("retrieving-captions");

    // Handle both array and single object formats
    if (result.result && (Array.isArray(result.result) || (typeof result.result === 'object' && result.result !== null))) {
      receivedCaption.value = true;
      currentRequestId.value = result.request;
      originalJsonContent.value = JSON.stringify(result.result);
      
      // Normalize result to work with both formats
      let resultData = result.result;
      let isArrayFormat = Array.isArray(result.result);
      
      // If it's a single object, check if it's Soniox format
      if (!isArrayFormat && typeof result.result === 'object') {
        // Single object format - could be Soniox or Google Cloud
        if (isSonioxFormat(result.result)) {
          // Soniox single object format: {id, text, tokens}
          console.log("📝 Detected Soniox format (single object) - using Soniox parser");
          originalText.value = extractTextFromSoniox(result.result);
          defaultOriginalText.value = extractTextFromSoniox(result.result);
          prebuildVideoSubtitle.value = subtitleGenerationSoniox(result.result);
          originalVideoSubtitle.value = subtitleGenerationSoniox(result.result);
          defaultOriginalVideoSubtitle.value = subtitleGenerationSoniox(result.result);
        } else {
          // Google Cloud single object - wrap in array
          const extractData = [result.result];
          originalText.value = extractText(extractData);
          defaultOriginalText.value = extractText(extractData);
          prebuildVideoSubtitle.value = subtitleGeneration(extractData);
          originalVideoSubtitle.value = subtitleGeneration(extractData);
          defaultOriginalVideoSubtitle.value = subtitleGeneration(extractData);
        }
      } else if (isArrayFormat) {
        // Array format - check first element
        const firstResult = result.result[0];
        if (isSonioxFormat(firstResult)) {
          console.log("📝 Detected Soniox format (array) - using Soniox parser");
          originalText.value = extractTextFromSoniox(firstResult);
          defaultOriginalText.value = extractTextFromSoniox(firstResult);
          prebuildVideoSubtitle.value = subtitleGenerationSoniox(firstResult);
          originalVideoSubtitle.value = subtitleGenerationSoniox(firstResult);
          defaultOriginalVideoSubtitle.value = subtitleGenerationSoniox(firstResult);
        } else {
          console.log("📝 Detected Google Cloud format (array) - using Google parser");
          originalText.value = extractText(result.result);
          defaultOriginalText.value = extractText(result.result);
          prebuildVideoSubtitle.value = subtitleGeneration(result.result);
          originalVideoSubtitle.value = subtitleGeneration(result.result);
          defaultOriginalVideoSubtitle.value = subtitleGeneration(result.result);
        }
      }
      if (videoPlayer.value) {
        videoPlayer.value?.ready(function () {
          videoPlayer.value?.addRemoteTextTrack(
            {
              src: `data:text/vtt;charset=utf-8,${encodeURIComponent(
                originalVideoSubtitle.value
              )}`,
              kind: "captions",
              srclang: originalLanguage.value.langCode,
              label: originalLanguage.value.langName,
              mode: "showing",
            },
            false
          );
        });
        //await translateText();
        videoPlayer.value?.src({
          type: "video/mp4",
          src: userInputVideoLink.value,
        });
        videoPlayer.value?.on("timeupdate", function () {});
        updateTextTrackStyles();
        const checkboxes = document.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach((checkbox: any) => {
          if (checkbox.id === "none-checkbox") {
            checkbox.checked = true;
          }
        });
      }
    } else {
      notReadyYet.value = true;
      /* setTimeout(() => {
        router.push("/");
      }, 3000); */
    }
    //processingLink.value = false;
    //processingStarted.value = false;
  } catch (error) {
    console.log(error);
    processingStarted.value = false;
    processingLink.value = false;
  }
};

const removeFile = () => {
  selectedVideo.value.name = "";
  selectedVideo.value.data = null;
};

const navToPayment = () => {
  try {
    activitesStore.setComingFrom("videotranscription");
    router.push("/pricing");
  } catch (error) {
    console.log("navToPayment", error);
  }
};

const selectTargetLanguage = async (lang: Language) => {
  if (lang.langCode == originalLanguage.value.langCode) {
    toast(t("original-and-translation-languages-must-be-different"), {
      autoClose: 1500,
      type: "error",
      theme: "light",
    });
    return;
  }
  translationLanguage.value = { ...lang };
  isTargetLanguageSelected.value = true;
  isTargetListOpen.value = false;
  closeList();
};

const oneTimeVideoFileFlow = async () => {
  const orderId = oneTimeUploadOrderId.value;
  if (!orderId || !selectedVideo.value?.data || !authStore.token) return;
  oneTimeUploadError.value = "";
  oneTimeUploading.value = true;
  processingStarted.value = true;
  ffmpegPercentage.value = 0;
  fileUploadPercentage.value = 0;
  progressBar.value = 0;
  if (notReadyYet.value) notReadyYet.value = false;
  processingStatus.value = `${t("video-file-processing-in-progress")} 0%`;
  try {
    if (!ffmpeg.loaded) {
      await ffmpeg.load({
        coreURL: await toBlobURL(`${ffmpegbaseURL}/ffmpeg-core.js`, "text/javascript"),
        wasmURL: await toBlobURL(`${ffmpegbaseURL}/ffmpeg-core.wasm`, "application/wasm"),
      });
    }
    ffmpeg.on("log", ({ message }) => {
      console.log(message);
    });
    ffmpeg.on("progress", (progress: { progress: number }) => {
      ffmpegPercentage.value = Math.round(progress.progress * 100);
      progressBar.value = ffmpegPercentage.value;
      processingStatus.value = `${t("video-file-processing-in-progress")} ${ffmpegPercentage.value}%`;
    });
    const inputFileName = selectedVideo.value.name;
    const outputFileName = `${inputFileName}.wav`;
    await ffmpeg.writeFile(inputFileName, new Uint8Array(selectedVideo.value.data as ArrayBuffer));
    await ffmpeg.exec(["-i", inputFileName, "-vn", "-acodec", "pcm_s16le", "-ar", "16000", "-ac", "1", outputFileName]);
    const outputData: any = await ffmpeg.readFile(outputFileName);
    progressBar.value = 0;
    const blob = new Blob([outputData], { type: "audio/wav" });
    processingStatus.value = `${t("uploading-video-to-the-server")} ${fileUploadPercentage.value}%`;
    const urlRes = await fetch(
      `${API_BASE_URL}${ONETIME_AUDIO_UPLOAD_URL}?orderId=${encodeURIComponent(orderId)}`,
      { headers: { Authorization: `Bearer ${authStore.token}` } }
    );
    const { url, fileName } = await urlRes.json();
    if (!url || !fileName) throw new Error("Failed to get upload URL");
    await uploadFileWithProgress(url, blob);
    const sourceLang = originalLanguage.value.langCode;
    if (!sourceLang) throw new Error("Source language is required");
    const confirmRes = await fetch(`${API_BASE_URL}${ONETIME_AUDIO_CONFIRM_UPLOAD}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authStore.token}` },
      body: JSON.stringify({ orderId, fileName, sourceLang, originalFileName: selectedVideo.value?.name || undefined }),
    });
    const confirmData = await confirmRes.json();
    if (!confirmRes.ok) throw new Error(confirmData.error || "Confirm failed");
    oneTimeUploadDone.value = true;
    notReadyYet.value = true;
  } catch (e) {
    oneTimeUploadError.value = (e as Error).message || "Upload failed";
    toast.error(oneTimeUploadError.value, { autoClose: 3000, theme: "light" });
  } finally {
    oneTimeUploading.value = false;
    if (!oneTimeUploadDone.value) processingStarted.value = false;
  }
};

const startProcessing = async () => {
  try {
    if (!authStore.token || authStore.token.length == 0) {
      authStore.setAuthPop(true);
      return;
    }
    if (originalLanguage.value.countryCode.length === 0) {
      toast(t("please-select-the-source-language-to"), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
      return;
    }
    if (oneTimeUploadOrderId.value) {
      await oneTimeVideoFileFlow();
      return;
    }
    const userSubscription = profileStore.subscriptionType;
    if (userSubscription === "FREE") {
      toast(t("subscription-required-please-upgrade-your-plan"), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
      return;
    }
    if (userSubscription != "CONSUMEASYOUPAY") {
      const currentTimestamp = new Date(new Date().toISOString());
      const subscriptionEndDate = new Date(profileStore.subscriptionEndDate);
      if (currentTimestamp > subscriptionEndDate) {
        toast(
          t("your-subscription-has-expired-please-renew-your-subscription"),
          {
            autoClose: 2500,
            type: "error",
            theme: "light",
          }
        );
        return;
      }
    }
    if (balanceNotEnough.value) {
      return;
    }
    ffmpegPercentage.value = 0;
    fileUploadPercentage.value = 0;
    progressBar.value = 0;
    fileUploaded.value = false;
    receivedCaption.value = false;
    processingStarted.value = true;
    if (notReadyYet.value) notReadyYet.value = false;
    processingStatus.value = `${t("video-file-processing-in-progress")} ${
      ffmpegPercentage.value
    }%`;
    originalVideoSubtitle.value = ``;
    defaultOriginalVideoSubtitle.value = ``;
    prebuildVideoSubtitle.value = ``;
    translatedVideoSubtitle.value = ``;
    defaultTranslatedVideoSubtitle.value = ``;
    currentRequestId.value = "";
    ffmpeg.on("log", ({ message }) => {
      console.log(message);
    });
    console.time("FFmpeg Start command");
    ffmpeg.on("progress", (progress) => {
      console.timeEnd("FFmpeg Start command");
      console.log(`Processing: ${Math.round(progress.progress * 100)}%`);
      ffmpegPercentage.value = Math.round(progress.progress * 100);
      progressBar.value = ffmpegPercentage.value;
      processingStatus.value = `${t("video-file-processing-in-progress")} ${
        ffmpegPercentage.value
      }%`;
    });
    if (!ffmpeg.loaded) {
      await ffmpeg.load({
        coreURL: await toBlobURL(
          `${ffmpegbaseURL}/ffmpeg-core.js`,
          "text/javascript"
        ),
        wasmURL: await toBlobURL(
          `${ffmpegbaseURL}/ffmpeg-core.wasm`,
          "application/wasm"
        ),
      });
    }
    const inputFileName = selectedVideo.value.name;
    const outputFileName = `${inputFileName}.wav`;

    if (!selectedVideo.value.data) {
      return;
    }
    //const fileData = new Uint8Array(selectedVideo.value.data as ArrayBuffer);
    //await ffmpeg.writeFile(inputFileName, fileData);
    await ffmpeg.writeFile(
      inputFileName,
      new Uint8Array(selectedVideo.value.data as ArrayBuffer)
    );

    await ffmpeg.exec([
      "-i",
      inputFileName,
      "-vn", // Disable video recording
      "-acodec",
      "pcm_s16le",
      "-ar",
      "16000", // Audio frequency
      "-ac",
      "1", // Audio channels
      outputFileName,
    ]);
    progressBar.value = 0;
    const outputData:any = await ffmpeg.readFile(outputFileName);
    /* const formData = new FormData();
    formData.append(
      "audioFile",
      new Blob([outputData], { type: "audio/wav" }),
      outputFileName
    );

    formData.append("languageCode", originalLanguage.value.langCode);
    formData.append("alternativeLanguageCodes", "");
    formData.append("requestType", "VIDEO");
    formData.append("duration", durationInUnits.toString()); */
    // Build query parameters for duplicate check
    const params = new URLSearchParams({
      originalname: selectedVideo.value.name,
      languageCode: originalLanguage.value.langCode,
      requestType: "VIDEO",
    });
    
    const response = await fetch(`${generateUploadUrl}?${params}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
    });
    const data = await response.json();
    if (!response.ok) {
      if (data.error) {
        switch (true) {
          case data.error === "Invalid token":
          case data.error === "Authorization header missing":
            toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
              autoClose: 2500,
              type: "error",
              theme: "light",
            });
            await new Promise((resolve) => setTimeout(resolve, 2500));
            authStore.logout();
            router.push("/signing");
            break;
          case data.error?.includes("already processing"):
            toast(
              data.error || t("file-already-processing-please-wait"),
              {
                autoClose: 5000,
                type: "error",
                theme: "light",
              }
            );
            processingStarted.value = false;
            break;
          default:
            break;
        }
      }
      return;
    }
    const { url: signedUrl, fileName } = data;
    const blob = new Blob([outputData], { type: "audio/wav" });
    processingStatus.value = `${t("uploading-video-to-the-server")} ${
      fileUploadPercentage.value
    }%`;
    /* const response = await fetch(transcriptAudioUrl, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
    }); */
    await uploadFileWithProgress(signedUrl, blob);
    const processData = {
      fileName,
      languageCode: originalLanguage.value.langCode,
      alternativeLanguageCodes: "",
      requestType: "VIDEO",
      originalname: selectedVideo.value.name,
    };

    const processRes = await fetch(transcriptAudioUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify(processData),
    });
    const result = await processRes.json();
    if (!result.ok) {
      if (result.error) {
        switch (result.error) {
          case "Authorization header missing":
          case "Invalid token":
            toast(
              t("your-session-has-expired-please-log-in-again-to-continue"),
              {
                autoClose: 2500,
                type: "error",
                theme: "light",
              }
            );
            await new Promise((resolve) => setTimeout(resolve, 2500));
            authStore.logout();
            router.push("/");
            break;
          case "The balance is insufficient":
            balanceNotEnough.value = true;
            toast(
              t(
                "insufficient-balance-please-recharge-your-account-to-complete-this-operation"
              ),
              {
                autoClose: 2500,
                type: "error",
                theme: "light",
              }
            );
            await new Promise((resolve) => setTimeout(resolve, 2500));
            window.location.reload();
            break;
          default:
            if (
              typeof result.error === "string" &&
              result.error.toLowerCase().includes("already processing")
            ) {
              toast(
                result.error || t("file-already-processing-please-wait"),
                {
                  autoClose: 5000,
                  type: "error",
                  theme: "light",
                }
              );
              processingStarted.value = false;
            } else {
              toast(t('were-fixing-a-technical-issue-so-please-try'), {
                autoClose: 3000,
                type: "error",
                theme: "light",
              });
            }
            break;
        }
      }
    }
    // Soniox endpoint returns immediately with job submission confirmation
    // Response format: { message: "Video file uploaded successfully! {fileId}", fileId: "uuid-here", status: "submitted" }
    // Or: { message: "Request received. Processing your file...", fileName: "uuid.wav" }
    // Backend will send notification when transcription is complete (same as Google Cloud approach)
    if (result.status === "submitted" || result.fileId || 
        (result.message && (result.message.includes("uploaded successfully") || 
                            result.message.includes("Request received") || 
                            result.message.includes("Processing your file")))) {
      fileUploaded.value = true;
      // Store the fileId for later retrieval (notification will contain this)
      if (result.fileId) {
        currentRequestId.value = result.fileId;
      }
    } else if (result.message == "Success") {
      // Fallback for old format (if still supported)
      fileUploaded.value = true;
    } else {
      console.error("Error uploading video file:", result);
      toast(t('were-fixing-a-technical-issue-so-please-try'), {
        autoClose: 2500,
        type: "error",
        theme: "light",
      });
      processingStarted.value = false;
      return;
    }
    notReadyYet.value = true;
  } catch (error) {
    console.log("startProcessing ================");
    console.log(error);
    processingStarted.value = false;
  }
};

const uploadFileWithProgress = (url: string, blob: any) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", "audio/wav"); // GCS expects this for signed URLs

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        fileUploadPercentage.value = Math.round(
          (event.loaded * 100) / event.total
        );
        processingStatus.value = `${t("uploading-video-to-the-server")} ${
          fileUploadPercentage.value
        }%`;
        progressBar.value = fileUploadPercentage.value;
      }
    });

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ message: "File uploaded to GCS" });
      } else {
        reject(new Error(`Failed to upload file: ${xhr.statusText}`));
      }
    };

    xhr.onerror = () =>
      reject(new Error("An error occurred during the file upload."));

    xhr.send(blob);
  });
};

const updateTextTrackStyles = () => {
  const styleElement = document.createElement("style");
  styleElement.innerHTML = `
    .vjs-text-track-display div {
      font-size: ${fontSizeClass.value.replace("text-", "")};
      font-family: ${fontFamily.value};
    }
  `;
  document.head.appendChild(styleElement);
};

const extractText = (data: any) => {
  try {
    let concatenatedTranscript = "";
    data[0].results.forEach((result: any) => {
      result.alternatives.forEach((alternative: any) => {
        concatenatedTranscript += alternative.transcript + " ";
      });
    });

    return concatenatedTranscript;
  } catch (error) {
    console.log("extractText");
    console.log(error);
    return "";
  }
};

function subtitleGeneration(speechToTextResponse: any): string {
  try {
    const captions: CaptionModel[] = [];
    const minBinSize = 2.5;
    const maxBinSize = 3.5;

    speechToTextResponse[0].results.forEach((result: any) => {
      let currentStartTime = 0;
      let transcript = "";

      for (let i = 0; i < result.alternatives[0].words.length; i++) {
        const word = result.alternatives[0].words[i].word;
        const wordStartSeconds =
          result.alternatives[0].words[i].startTime.seconds || 0;
        const nextWordStartSeconds =
          result.alternatives[0].words[i + 1]?.startTime.seconds || 0;
        const ndnextWordStartSeconds =
          result.alternatives[0].words[i + 2]?.startTime.seconds || 0;

        if (transcript.trim() === "") {
          currentStartTime = wordStartSeconds;
        }

        transcript = transcript + " " + word;

        const duration = nextWordStartSeconds - currentStartTime;
        const ndDuration =
          ndnextWordStartSeconds - nextWordStartSeconds + duration;

        if (
          (minBinSize <= duration && duration <= maxBinSize) ||
          ndDuration > maxBinSize
        ) {
          const newCaption: CaptionModel = {
            start: formatTime(currentStartTime),
            dur: formatTime(nextWordStartSeconds),
            text: transcript.trim(),
          };

          captions.push(newCaption);

          currentStartTime = nextWordStartSeconds;
          transcript = "";
        }
      }

      if (transcript.trim() !== "") {
        const lastWordEndSeconds =
          result.alternatives[0].words[result.alternatives[0].words.length - 1]
            .endTime.seconds;
        const duration = lastWordEndSeconds - currentStartTime;

        if (duration > 0) {
          const newCaption: CaptionModel = {
            start: formatTime(currentStartTime),
            dur: formatTime(lastWordEndSeconds),
            text: transcript.trim(),
          };
          captions.push(newCaption);
        }
      }
    });

    // Format captions into WebVTT
    let webvttContent = "WEBVTT\n\n";
    captions.forEach((caption, index) => {
      webvttContent += `${index + 1}\n${caption.start} --> ${caption.dur}\n${
        caption.text
      }\n\n`;
    });

    return webvttContent;
  } catch (error) {
    console.error("Error processing captions:", error);
    return "";
  }
}

function isValidGoogleDriveLink(link: any) {
  const regex =
    /^https:\/\/(?:drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?id=|uc\?export=download&id=|drive\/folders\/|drive\/u\/\d\/folders\/)|docs\.google\.com\/(?:uc\?id=|document\/d\/|spreadsheets\/d\/|presentation\/d\/|forms\/d\/))([a-zA-Z0-9_-]+)/;
  return regex.test(link);
}

const transcriptGoogleDriveVideo = async (extendPayment?: boolean) => {
  try {
    processingLink.value = true;
    processingStarted.value = true;
    notReadyYet.value = false;
    processingStatus.value = t("send-a-request-to-the-server");
    const response = await fetch(transcriptGoogleDriveVideoUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.token}`,
        "ngrok-skip-browser-warning": "69420",
      },
      body: JSON.stringify({
        gdriveLink: userInputVideoLink.value,
        originalLang: originalLanguage.value.langCode,
        extendPayment,
      }),
    });
    const result = await response.json();
    if (result.error) {
      switch (result.error) {
        case "Authorization header missing":
        case "Invalid token":
          toast(t("your-session-has-expired-please-log-in-again-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
          await new Promise((resolve) => setTimeout(resolve, 2500));
          authStore.logout();
          router.push("/");
          break;
        case "The balance is insufficient":
          const userSubscription = profileStore.subscriptionType;
          const videoUnits = result.videoUnits || 0;
          if (userSubscription === "CONSUMEASYOUPAY") {
            const cost = sttCostFromDeposit(videoUnits / 2);
            const currentTimestamp = new Date(new Date().toISOString());
            const subscriptionEndDate = new Date(
              profileStore.subscriptionEndDate
            );
            if (currentTimestamp > subscriptionEndDate) {
              balanceNotEnough.value = true;
              toast(
                t("your-balance-is-insufficient-please-recharge-to-continue"),
                {
                  autoClose: 2500,
                  type: "error",
                  theme: "light",
                }
              );
              await new Promise((resolve) => setTimeout(resolve, 2500));
              window.location.reload();
              break;
            }

            const shortfallCost = cost - profileStore.userDeposit;
            const shortfallChars = Math.ceil(
              shortfallCost / servicesPricing.speechToText
            );
            if (shortfallChars > profileStore.speechToTextVolume) {
              balanceNotEnough.value = true;
              toast(
                t("your-balance-is-insufficient-please-recharge-to-continue"),
                {
                  autoClose: 2500,
                  type: "error",
                  theme: "light",
                }
              );
              await new Promise((resolve) => setTimeout(resolve, 2500));
              window.location.reload();
              break;
            }

            modalMessage.value = t("your-deposit-balance-is-not-enough");
            showPaymentModal.value = true;
            isPaymentProcessingLink.value = true;
            processingStarted.value = false;
            processingLink.value = false;
            break;
          } else {
            const shortfallCost =
              videoUnits / 2 - profileStore.speechToTextVolume;
            const shortfallChars = sttCostFromDeposit(shortfallCost);
            if (shortfallChars > profileStore.userDeposit) {
              balanceNotEnough.value = true;
              toast(
                t("your-balance-is-insufficient-please-recharge-to-continue"),
                {
                  autoClose: 2500,
                  type: "error",
                  theme: "light",
                }
              );
              await new Promise((resolve) => setTimeout(resolve, 2500));
              window.location.reload();
              break;
            }

            modalMessage.value = t("your-subscription-balance-is-not");
            showPaymentModal.value = true;
            processingStarted.value = false;
            isPaymentProcessingLink.value = true;
            processingLink.value = false;
            break;
          }
        default:
          if (
            typeof result.error === "string" &&
            result.error.toLowerCase().includes("already processing")
          ) {
            toast(
              result.error || t("file-already-processing-please-wait"),
              {
                autoClose: 5000,
                type: "error",
                theme: "light",
              }
            );
            processingStarted.value = false;
            processingLink.value = false;
          } else if (
            typeof result.error === "string" &&
            result.error.toLowerCase().includes("Audio duration exceeds maximum")
          ) {
            toast(t("this-clip-exceeds-the-8-h"), {
              autoClose: 3500,
              type: "warning",
              theme: "light",
            });
          } else {
            toast(t('were-fixing-a-technical-issue-so-please-try'), {
              autoClose: 3000,
              type: "error",
              theme: "light",
            });
            processingStarted.value = false;
            await new Promise((resolve) => setTimeout(resolve, 2500));
            window.location.reload();
          }
          break;
      }
    }
    processingStatus.value = t("retrieving-captions");
    // Handle both array and single object formats
    if (result.result && (Array.isArray(result.result) || (typeof result.result === 'object' && result.result !== null))) {
      receivedCaption.value = true;
      currentRequestId.value = result.request;
      originalJsonContent.value = JSON.stringify(result.result);
      
      // Normalize result to work with both formats
      let resultData = result.result;
      let isArrayFormat = Array.isArray(result.result);
      
      // If it's a single object, check if it's Soniox format
      if (!isArrayFormat && typeof result.result === 'object') {
        // Single object format - could be Soniox or Google Cloud
        if (isSonioxFormat(result.result)) {
          // Soniox single object format: {id, text, tokens}
          console.log("📝 Detected Soniox format (single object) - using Soniox parser");
          originalText.value = extractTextFromSoniox(result.result);
          defaultOriginalText.value = extractTextFromSoniox(result.result);
          prebuildVideoSubtitle.value = subtitleGenerationSoniox(result.result);
          originalVideoSubtitle.value = subtitleGenerationSoniox(result.result);
          defaultOriginalVideoSubtitle.value = subtitleGenerationSoniox(result.result);
        } else {
          // Google Cloud single object - wrap in array
          const extractData = [result.result];
          originalText.value = extractText(extractData);
          defaultOriginalText.value = extractText(extractData);
          prebuildVideoSubtitle.value = subtitleGeneration(extractData);
          originalVideoSubtitle.value = subtitleGeneration(extractData);
          defaultOriginalVideoSubtitle.value = subtitleGeneration(extractData);
        }
      } else if (isArrayFormat) {
        // Array format - check first element
        const firstResult = result.result[0];
        if (isSonioxFormat(firstResult)) {
          console.log("📝 Detected Soniox format (array) - using Soniox parser");
          originalText.value = extractTextFromSoniox(firstResult);
          defaultOriginalText.value = extractTextFromSoniox(firstResult);
          prebuildVideoSubtitle.value = subtitleGenerationSoniox(firstResult);
          originalVideoSubtitle.value = subtitleGenerationSoniox(firstResult);
          defaultOriginalVideoSubtitle.value = subtitleGenerationSoniox(firstResult);
        } else {
          console.log("📝 Detected Google Cloud format (array) - using Google parser");
          originalText.value = extractText(result.result);
          defaultOriginalText.value = extractText(result.result);
          prebuildVideoSubtitle.value = subtitleGeneration(result.result);
          originalVideoSubtitle.value = subtitleGeneration(result.result);
          defaultOriginalVideoSubtitle.value = subtitleGeneration(result.result);
        }
      }
      if (videoPlayer.value) {
        videoPlayer.value?.ready(function () {
          videoPlayer.value?.addRemoteTextTrack(
            {
              src: `data:text/vtt;charset=utf-8,${encodeURIComponent(
                originalVideoSubtitle.value
              )}`,
              kind: "captions",
              srclang: originalLanguage.value.langCode,
              label: originalLanguage.value.langName,
              mode: "showing",
            },
            false
          );
        });
        //await translateText();
        const googleDriveFileId = extractFileIdFromDriveLink(
          userInputVideoLink.value
        );
        console.log(import.meta.env.VITE_GOOGLE_DRIVE_API);
        const googleDriveUrl = `https://www.googleapis.com/drive/v3/files/${googleDriveFileId}?alt=media&key=${
          import.meta.env.VITE_GOOGLE_DRIVE_API
        }`;

        videoPlayer.value?.src({
          type: "video/mp4",
          src: googleDriveUrl,
        });
        videoPlayer.value?.on("timeupdate", function () {});
        updateTextTrackStyles();
        const checkboxes = document.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach((checkbox: any) => {
          if (checkbox.id === "none-checkbox") {
            checkbox.checked = true;
          }
        });
      }
      return;
    } else {
      notReadyYet.value = true;
      /* setTimeout(() => {
        router.push("/");
      }, 3000); */
    }
    //processingLink.value = false;
    //processingStarted.value = false;
  } catch (error) {
    console.log(error);
    processingStarted.value = false;
    processingLink.value = false;
  }
};

function sttCostFromDeposit(minutes: number) {
  return minutes * servicesPricing.speechToText;
}

const computeAudioCost = async () => {
  try {
    computePricing.value = true;
    balanceNotEnough.value = false;
    const userSubscription = profileStore.subscriptionType;
    if (userSubscription === "CONSUMEASYOUPAY") {
      const cost = sttCostFromDeposit(durationInUnits / 2);
      if (profileStore.userDeposit < cost) {
        const currentTimestamp = new Date(new Date().toISOString());
        const subscriptionEndDate = new Date(profileStore.subscriptionEndDate);
        if (currentTimestamp > subscriptionEndDate) {
          toast(t("your-balance-is-insufficient-please-recharge-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
        }
        const shortfallCost = cost - profileStore.userDeposit;
        const shortfallChars = Math.ceil(
          shortfallCost / servicesPricing.speechToText
        );
        if (shortfallChars > profileStore.speechToTextVolume) {
          toast(t("your-balance-is-insufficient-please-recharge-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
        } else {
          modalMessage.value = t("your-deposit-balance-is-not-enough");
          showPaymentModal.value = true;
        }
        balanceNotEnough.value = true;
      }
    } else {
      if (profileStore.speechToTextVolume < durationInUnits / 2) {
        const shortfallCost =
          durationInUnits / 2 - profileStore.speechToTextVolume;
        const shortfallChars = sttCostFromDeposit(shortfallCost);
        if (shortfallChars > profileStore.userDeposit) {
          toast(t("your-balance-is-insufficient-please-recharge-to-continue"), {
            autoClose: 2500,
            type: "error",
            theme: "light",
          });
        } else {
          modalMessage.value = t("your-subscription-balance-is-not");
          showPaymentModal.value = true;
        }
        balanceNotEnough.value = true;
      }
    }
    computePricing.value = false;
  } catch (error) {
    console.log(error);
    computePricing.value = false;
    balanceNotEnough.value = false;
  }
};

const extractFileIdFromDriveLink = (driveLink: any) => {
  const regex = /\/d\/(.*?)(\/|$)/;
  const match = driveLink.match(regex);
  return match ? match[1] : null;
};

function formatTime(seconds: number): string {
  const date = new Date(seconds * 1000);
  const hours = date.getUTCHours().toString().padStart(2, "0");
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const secondsFormatted = date.getSeconds().toString().padStart(2, "0");
  const milliseconds = (date.getMilliseconds() / 1000).toFixed(3).slice(2, 5);
  return `${hours}:${minutes}:${secondsFormatted}.${milliseconds}`;
}

// Helper function to detect if response is Soniox format
function isSonioxFormat(data: any): boolean {
  // Soniox format has: id, text, tokens array
  // Google Cloud format has: results array with alternatives
  if (!data || typeof data !== 'object') {
    return false;
  }
  
  // Check if it's Soniox format (has id, text, and tokens array)
  if (data.id !== undefined && data.text !== undefined && Array.isArray(data.tokens)) {
    return true;
  }
  
  // Check if it's Google Cloud format (has results array with alternatives)
  if (data.results && Array.isArray(data.results) && data.results.length > 0) {
    if (data.results[0].alternatives && Array.isArray(data.results[0].alternatives)) {
      return false;
    }
  }
  
  // Check if it's Google Cloud format (array with results)
  if (Array.isArray(data) && data.length > 0 && data[0].results) {
    return false;
  }
  
  return false;
}

// New method to extract text from Soniox format
const extractTextFromSoniox = (sonioxData: any): string => {
  try {
    // Soniox format has text field directly
    if (sonioxData && typeof sonioxData === 'object' && sonioxData.text) {
      return sonioxData.text || "";
    }
    
    // If text field is not available, try to reconstruct from tokens
    if (sonioxData && sonioxData.tokens && Array.isArray(sonioxData.tokens)) {
      let text = "";
      sonioxData.tokens.forEach((token: any) => {
        if (token.text) {
          text += token.text;
        }
      });
      return text.trim();
    }
    
    return "";
  } catch (error) {
    console.error("Error extracting text from Soniox:", error);
    return "";
  }
};

// Parse Soniox transcription format
function parseSonioxTranscription(sonioxResponse: any): CaptionModel[] {
  try {
    const captions: CaptionModel[] = [];
    const minBinSize = 3.5;
    const maxBinSize = 4.5;
    
    if (!sonioxResponse.tokens || !Array.isArray(sonioxResponse.tokens)) {
      console.warn("Soniox response missing tokens array");
      return captions;
    }

    let currentSpeaker: number | null = null;
    let currentStartTime = 0;
    let transcript = "";
    let speakerSegments: Array<{
      word: string;
      speakerTag: number | null;
      startTime: number;
    }> = [];

    // Group tokens into words
    const words: Array<{
      text: string;
      startMs: number;
      endMs: number;
      speaker: number | null;
    }> = [];
    
    let currentWord = "";
    let wordStartMs = 0;
    let wordEndMs = 0;
    let wordSpeaker: number | null = null;

    sonioxResponse.tokens.forEach((token: any) => {
      const tokenText = token.text || "";
      const tokenStartMs = token.start_ms || 0;
      const tokenEndMs = token.end_ms || 0;
      const tokenSpeaker = token.speaker !== null && token.speaker !== undefined ? (typeof token.speaker === 'string' ? parseInt(token.speaker) : token.speaker) : null;

      const hasLeadingSpace = tokenText.trimStart() !== tokenText;
      const trimmedTokenText = tokenText.trimStart();
      
      if (hasLeadingSpace) {
        if (currentWord.trim()) {
          words.push({
            text: currentWord.trim(),
            startMs: wordStartMs,
            endMs: wordEndMs,
            speaker: wordSpeaker
          });
        }
        currentWord = trimmedTokenText;
        wordStartMs = tokenStartMs;
        wordEndMs = tokenEndMs;
        wordSpeaker = tokenSpeaker;
      } else {
        if (!currentWord) {
          wordStartMs = tokenStartMs;
          wordSpeaker = tokenSpeaker;
        }
        currentWord += tokenText;
        wordEndMs = tokenEndMs;
        if (tokenSpeaker !== null) {
          wordSpeaker = tokenSpeaker;
        }
      }
    });

    if (currentWord.trim()) {
      words.push({
        text: currentWord.trim(),
        startMs: wordStartMs,
        endMs: wordEndMs,
        speaker: wordSpeaker
      });
    }

    const getMostCommonSpeaker = (segments: Array<{speakerTag: number | null}>): number | null => {
      const speakerCounts: Record<number, number> = {};
      let maxCount = 0;
      let mostCommonSpeaker: number | null = null;

      segments.forEach(segment => {
        if (segment.speakerTag !== null && segment.speakerTag !== undefined) {
          speakerCounts[segment.speakerTag] = (speakerCounts[segment.speakerTag] || 0) + 1;
          if (speakerCounts[segment.speakerTag] > maxCount) {
            maxCount = speakerCounts[segment.speakerTag];
            mostCommonSpeaker = segment.speakerTag;
          }
        }
      });

      if (mostCommonSpeaker === null) {
        const firstSpeaker = segments.find(s => s.speakerTag !== null && s.speakerTag !== undefined);
        if (firstSpeaker) {
          mostCommonSpeaker = firstSpeaker.speakerTag;
        }
      }

      return mostCommonSpeaker;
    };

    if (words.length === 0) {
      return captions;
    }
    
    words.forEach((word, i) => {
      const wordStartSeconds = word.startMs / 1000;
      const nextWord = words[i + 1];
      const nextWordStartSeconds = nextWord ? (nextWord.startMs / 1000) : null;
      
      const wordText = word.text;
      const speakerTag = word.speaker;

      if (transcript.trim() === "") {
        currentStartTime = wordStartSeconds;
        currentSpeaker = speakerTag;
      }

      const speakerChanged = currentSpeaker !== null && speakerTag !== null && currentSpeaker !== speakerTag;
      
      if (speakerChanged && transcript.trim() !== "" && speakerSegments.length > 0) {
        const duration = wordStartSeconds - currentStartTime;
        if (duration >= 0.3) {
          const captionSpeaker = getMostCommonSpeaker(speakerSegments);
          
          const newCaption: CaptionModel = {
            start: formatTime(currentStartTime),
            dur: formatTime(wordStartSeconds),
            text: transcript.trim(),
            speakerTag: captionSpeaker,
            words: speakerSegments.map(s => ({
              word: s.word,
              speakerTag: s.speakerTag,
              startTime: s.startTime
            }))
          };

          captions.push(newCaption);

          currentStartTime = wordStartSeconds;
          transcript = "";
          speakerSegments = [];
          currentSpeaker = speakerTag;
        }
      }

      transcript = transcript + " " + wordText;
      
      speakerSegments.push({
        word: wordText,
        speakerTag: speakerTag,
        startTime: wordStartSeconds
      });

      if (speakerTag !== null) {
        currentSpeaker = speakerTag;
      }
      
      if (nextWordStartSeconds !== null) {
        const duration = nextWordStartSeconds - currentStartTime;
        const ndnextWord = words[i + 2];
        const ndnextWordStartSeconds = ndnextWord ? (ndnextWord.startMs / 1000) : null;
        const ndDuration = ndnextWordStartSeconds 
          ? (ndnextWordStartSeconds - nextWordStartSeconds + duration) 
          : null;

        const shouldSplit = 
          (minBinSize <= duration && duration <= maxBinSize) ||
          (ndDuration && ndDuration > maxBinSize);

        if (shouldSplit) {
          const captionSpeaker = getMostCommonSpeaker(speakerSegments);
          
          const newCaption: CaptionModel = {
            start: formatTime(currentStartTime),
            dur: formatTime(nextWordStartSeconds),
            text: transcript.trim(),
            speakerTag: captionSpeaker,
            words: speakerSegments.map(s => ({
              word: s.word,
              speakerTag: s.speakerTag,
              startTime: s.startTime
            }))
          };

          captions.push(newCaption);

          currentStartTime = nextWordStartSeconds;
          transcript = "";
          speakerSegments = [];
          currentSpeaker = speakerTag;
        }
      }
    });

    if (transcript.trim() !== "" && words.length > 0) {
      const lastWord = words[words.length - 1];
      const lastWordEndSeconds = lastWord.endMs / 1000;
      const duration = lastWordEndSeconds - currentStartTime;

      if (duration > 0) {
        const captionSpeaker = getMostCommonSpeaker(speakerSegments);
        
        const newCaption: CaptionModel = {
          start: formatTime(currentStartTime),
          dur: formatTime(lastWordEndSeconds),
          text: transcript.trim(),
          speakerTag: captionSpeaker,
          words: speakerSegments.map(s => ({
            word: s.word,
            speakerTag: s.speakerTag,
            startTime: s.startTime
          }))
        };
        captions.push(newCaption);
      }
    }

    return captions;
  } catch (error) {
    console.error("Error parsing Soniox transcription:", error);
    return [];
  }
}

// Generate WebVTT from Soniox format
function subtitleGenerationSoniox(sonioxResponse: any): string {
  try {
    const captions = parseSonioxTranscription(sonioxResponse);
    
    // Format captions into WebVTT
    let webvttContent = "WEBVTT\n\n";
    captions.forEach((caption, index) => {
      webvttContent += `${index + 1}\n${caption.start} --> ${caption.dur}\n${
        caption.text
      }\n\n`;
    });

    return webvttContent;
  } catch (error) {
    console.error("Error generating WebVTT from Soniox:", error);
    return "";
  }
}

// Title is set by the route/page component, not by this reusable component

function resetOneTimeState() {
  showOneTimePayPanel.value = false;
  oneTimeUploadOrderId.value = null;
  oneTimeResultPendingOrderId.value = null;
  oneTimeUploadSourceLang.value = "";
}

defineExpose({ resetOneTimeState });
</script>
